/**
 * Inline Add to Cart for Product Grid Items
 * Handles variant selection, quantity picking, and direct add to cart
 */

(function() {
  'use strict';

  // Initialize all product cards
  function initProductCards() {
    const productCards = document.querySelectorAll('[data-product-item]');
    productCards.forEach(card => {
      initProductCard(card);
    });
  }

  // Initialize a single product card
  function initProductCard(card) {
    // Prevent double initialization
    if (card.dataset.inlineAtcInitialized === 'true') return;
    card.dataset.inlineAtcInitialized = 'true';
    
    const addToCartBtn = card.querySelector('[data-inline-add-to-cart]');
    if (!addToCartBtn) return;

    const variantsDataEl = card.querySelector('[data-product-variants]');
    const inlineOptions = card.querySelector('[data-inline-options]');
    const quantityInput = card.querySelector('[data-quantity-input]');
    const quantityMinus = card.querySelector('[data-quantity-minus]');
    const quantityPlus = card.querySelector('[data-quantity-plus]');
    const quantityMaxNote = card.querySelector('[data-quantity-max-note]');
    const variantSelects = card.querySelectorAll('[data-variant-option]');

    let variantsData = null;
    if (variantsDataEl) {
      try {
        variantsData = JSON.parse(variantsDataEl.textContent);
      } catch (e) {
        console.error('Error parsing variants data:', e);
      }
    }

    // Resolve currently selected variant from option radios
    function getSelectedVariant() {
      if (!variantsData || !variantsData.variants || variantsData.variants.length === 0) return null;
      const optionGroups = card.querySelectorAll('[data-option-buttons]');
      if (optionGroups.length === 0) {
        // No option groups (single default variant)
        const variantIdStr = addToCartBtn.dataset.variantId;
        const variantId = variantIdStr ? parseInt(variantIdStr) : null;
        if (variantId) {
          return variantsData.variants.find(v => v.id === variantId) || null;
        }
        return variantsData.variants.find(v => v.available) || variantsData.variants[0] || null;
      }
      const selectedByIndex = [];
      optionGroups.forEach(group => {
        const index = parseInt(group.getAttribute('data-option-index'), 10);
        const checked = group.querySelector('[data-variant-option]:checked');
        if (checked) {
          selectedByIndex[index] = checked.value;
        }
      });
      return variantsData.variants.find(variant => {
        return selectedByIndex.every((val, idx) => variant[`option${idx + 1}`] === val);
      }) || null;
    }

    // Get quantity increment for current variant
    function getQuantityIncrement() {
      // Always use increment of 1 (quantity rules disabled site-wide)
      return 1;
    }

    // Get minimum quantity for current variant
    function getMinimumQuantity() {
      // Always use minimum of 1 (quantity rules disabled site-wide)
      return 1;
    }

    // Helper to get max available for current variant
    function getCurrentMaxQuantity() {
      const variant = getSelectedVariant();
      if (variant) {
        // Prefer explicit inventory map if provided
        if (variantsData && variantsData.inventory && variantsData.inventory[variant.id]) {
          const inv = variantsData.inventory[variant.id];
          if (inv.inventory_management && inv.inventory_policy !== 'continue') {
            const q = parseInt(inv.inventory_quantity, 10);
            if (!isNaN(q)) return Math.max(0, q);
          } else {
            return null;
          }
        }
        // Fallback to variant object fields
        if (variant.inventory_management && variant.inventory_policy !== 'continue') {
          if (typeof variant.inventory_quantity === 'number') {
            return Math.max(0, variant.inventory_quantity);
          }
        } else {
          return null;
        }
      }
      // Fallback to server-rendered max/data attribute when variant data is missing/incomplete
      if (quantityInput) {
        const dataMax = quantityInput.getAttribute('data-max-quantity');
        const attrMax = quantityInput.getAttribute('max');
        const parsed = parseInt(dataMax || attrMax || '', 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
      return null;
    }

    function applyMaxToQuantityInput(showUserMaxFeedback) {
      if (!quantityInput) return;
      const maxQty = getCurrentMaxQuantity();
      if (maxQty && maxQty > 0) {
        quantityInput.max = String(maxQty);
        quantityInput.setAttribute('data-max-quantity', String(maxQty));
        // Clamp current value
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > maxQty) {
          quantityInput.value = maxQty;
        }
        // Disable plus if at max
        if (quantityPlus) {
          quantityPlus.disabled = currentValue >= maxQty;
          if (currentValue >= maxQty && showUserMaxFeedback) {
            quantityPlus.setAttribute('aria-disabled', 'true');
            quantityPlus.title = `Maximum ${maxQty} reached`;
          } else {
            quantityPlus.removeAttribute('aria-disabled');
            quantityPlus.removeAttribute('title');
          }
        }
        // Toggle max note
        if (quantityMaxNote) {
          if (showUserMaxFeedback && currentValue >= maxQty) {
            quantityMaxNote.style.display = '';
          } else {
            quantityMaxNote.style.display = 'none';
          }
        }
      } else {
        // Remove cap
        quantityInput.removeAttribute('max');
        quantityInput.setAttribute('data-max-quantity', '');
        if (quantityPlus) {
          quantityPlus.disabled = false;
          quantityPlus.removeAttribute('aria-disabled');
          quantityPlus.removeAttribute('title');
        }
        if (quantityMaxNote) {
          quantityMaxNote.style.display = 'none';
        }
      }
    }

    // Quantity controls
    if (quantityMinus) {
      quantityMinus.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentValue = parseInt(quantityInput.value) || 1;
        const increment = getQuantityIncrement();
        const minQty = getMinimumQuantity();
        const next = currentValue - increment;
        if (next >= minQty) {
          quantityInput.value = next;
        }
        // Re-evaluate plus disabled state against max (no user feedback)
        applyMaxToQuantityInput(false);
      });
    }

    if (quantityPlus) {
      quantityPlus.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const currentValue = parseInt(quantityInput.value) || 1;
        const maxQty = getCurrentMaxQuantity();
        const increment = getQuantityIncrement();
        const next = currentValue + increment;
        if (maxQty && maxQty > 0) {
          quantityInput.value = next > maxQty ? maxQty : next;
        } else {
          quantityInput.value = next;
        }
        // Re-evaluate plus disabled state against max (show feedback if capped)
        applyMaxToQuantityInput(true);
      });
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        const value = parseInt(quantityInput.value);
        const maxQty = getCurrentMaxQuantity();
        const minQty = getMinimumQuantity();
        const increment = getQuantityIncrement();
        if (isNaN(value) || value < minQty) {
          quantityInput.value = minQty;
        } else {
          // Round to nearest valid increment
          const remainder = (value - minQty) % increment;
          if (remainder !== 0) {
            quantityInput.value = value - remainder;
          }
        }
        const adjustedValue = parseInt(quantityInput.value);
        if (maxQty && maxQty > 0 && adjustedValue > maxQty) {
          // Round down to max valid quantity respecting increment
          const maxValidQty = Math.floor((maxQty - minQty) / increment) * increment + minQty;
          quantityInput.value = maxValidQty;
        }
        applyMaxToQuantityInput(false);
      });
      // Initialize quantity to minimum on load
      const minQty = getMinimumQuantity();
      if (parseInt(quantityInput.value) < minQty) {
        quantityInput.value = minQty;
      }
      // Initialize max from initial variant
      applyMaxToQuantityInput(false);
    }

    // Variant selection
    if (variantSelects.length > 0 && variantsData) {
      // Update variant availability states
      updateVariantAvailability(card, variantsData);
      
      variantSelects.forEach(select => {
        select.addEventListener('change', () => {
          updateVariantAvailability(card, variantsData);
          updateSelectedVariant(addToCartBtn, variantSelects, variantsData);
          // Update quantity max on variant change (no user feedback)
          if (quantityInput) {
            const minQty = getMinimumQuantity();
            quantityInput.value = minQty;
          }
          applyMaxToQuantityInput(false);
        });
      });

      // Set initial variant based on current selections
      updateSelectedVariant(addToCartBtn, variantSelects, variantsData);
    } else if (variantsData && variantsData.variants && variantsData.variants.length > 0) {
      // For products without variant options (default variant only), ensure variant ID is set
      const firstAvailableVariant = variantsData.variants.find(v => v.available);
      if (firstAvailableVariant) {
        addToCartBtn.dataset.variantId = firstAvailableVariant.id;
      } else if (variantsData.variants[0]) {
        addToCartBtn.dataset.variantId = variantsData.variants[0].id;
      }
      // Apply initial max when variant set (no user feedback)
      applyMaxToQuantityInput(false);
    }

    // Add to cart functionality
    addToCartBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (addToCartBtn.disabled || addToCartBtn.classList.contains('adding')) {
        return;
      }

      const variantId = addToCartBtn.dataset.variantId;
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

      if (!variantId) {
        console.error('No variant ID found');
        return;
      }

      await addToCart(addToCartBtn, variantId, quantity);
    });

    // Prevent clicks on inline options from bubbling to product link
    if (inlineOptions) {
      inlineOptions.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }
  }

  // Update variant availability - disable unavailable options
  function updateVariantAvailability(card, variantsData) {
    const optionButtons = card.querySelectorAll('[data-option-buttons]');
    
    optionButtons.forEach(optionContainer => {
      const optionIndex = parseInt(optionContainer.dataset.optionIndex);
      const allRadios = card.querySelectorAll('[data-variant-option]');
      
      // Get currently selected values for other options
      const currentSelections = Array.from(allRadios)
        .filter(radio => radio.checked)
        .map(radio => radio.value);
      
      // Check each option value in this option group
      const radiosInGroup = optionContainer.querySelectorAll('[data-variant-option]');
      radiosInGroup.forEach(radio => {
        const optionValue = radio.dataset.optionValue;
        
        // Build a test selection array with this option value
        const testSelections = [...currentSelections];
        testSelections[optionIndex] = optionValue;
        
        // Check if any variant matches this combination and is available
        const hasAvailableVariant = variantsData.variants.some(variant => {
          // Check if this variant matches the current selections
          const matches = testSelections.every((value, idx) => {
            return variant[`option${idx + 1}`] === value;
          });
          
          return matches && variant.available;
        });
        
        // Enable or disable based on availability
        const label = radio.closest('.productitem--variant-button-label');
        if (hasAvailableVariant) {
          label.classList.remove('disabled');
          radio.disabled = false;
        } else {
          label.classList.add('disabled');
          radio.disabled = true;
        }
      });
    });
  }

  // Update selected variant based on option selections
  function updateSelectedVariant(button, selects, variantsData) {
    const selectedOptions = Array.from(selects)
      .filter(select => select.checked)
      .map(select => select.value);
    
    // Find matching variant
    const matchingVariant = variantsData.variants.find(variant => {
      return selectedOptions.every((option, index) => {
        const variantOption = variant[`option${index + 1}`];
        return variantOption === option;
      });
    });

    if (matchingVariant) {
      button.dataset.variantId = matchingVariant.id;
      
      const textSpan = button.querySelector('.atc-button--text');
      
      // Update button state based on availability
      if (matchingVariant.available) {
        button.disabled = false;
        button.classList.remove('disabled');
        if (textSpan) {
          textSpan.textContent = 'Add to Cart';
        }
      } else {
        button.disabled = true;
        button.classList.add('disabled');
        if (textSpan) {
          textSpan.textContent = 'Out of Stock';
        }
      }
    } else {
      // No matching variant found
      console.warn('No matching variant found for selections:', selectedOptions);
      button.disabled = true;
      button.classList.add('disabled');
    }
  }

  // Add to cart function
  async function addToCart(button, variantId, quantity) {
    // Validate variant ID
    if (!variantId || variantId === 'undefined' || variantId === 'null') {
      console.error('Invalid variant ID:', variantId);
      alert('Please select a valid product option');
      return;
    }

    // Get settings
    const settingsEl = button.closest('[data-product-item]').querySelector('[data-quick-buy-settings]');
    let settings = { cart_redirection: false };
    if (settingsEl) {
      try {
        settings = JSON.parse(settingsEl.textContent);
      } catch (e) {
        console.error('Error parsing settings:', e);
      }
    }

    // Update button state
    button.classList.add('adding');
    button.disabled = true;
    const textSpan = button.querySelector('.atc-button--text');
    const iconSpan = button.querySelector('.atc-button--icon');
    const originalText = textSpan ? textSpan.textContent : '';

    if (textSpan) textSpan.textContent = 'Adding...';
    if (iconSpan) iconSpan.style.display = 'inline-block';

    try {
      const formData = {
        items: [{
          id: parseInt(variantId),
          quantity: quantity
        }]
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        // Get error details from response
        let errorMessage = 'Failed to add to cart';
        try {
          const errorData = await response.json();
          errorMessage = errorData.description || errorData.message || errorMessage;
        } catch (e) {
          // If we can't parse error, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Success feedback
      if (textSpan) textSpan.textContent = 'Added!';
      
      // Update cart count if there's a cart drawer/indicator
      updateCartCount();

      // Trigger cart drawer or redirect
      if (settings.cart_redirection) {
        window.location.href = '/cart';
      } else {
        // Try to open cart drawer if available
        const cartDrawerTrigger = document.querySelector('[data-cart-trigger]');
        if (cartDrawerTrigger) {
          cartDrawerTrigger.click();
        }
      }

      // Reset button after delay
      setTimeout(() => {
        button.classList.remove('adding');
        button.disabled = false;
        if (textSpan) textSpan.textContent = originalText;
        if (iconSpan) iconSpan.style.display = 'none';
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Error feedback
      if (textSpan) {
        textSpan.textContent = error.message.includes('sold out') || error.message.includes('available') 
          ? 'Out of Stock' 
          : 'Error - Try Again';
      }
      
      // Show alert for user feedback
      if (error.message.includes('sold out') || error.message.includes('available')) {
        alert('Sorry, this product is currently out of stock or unavailable.');
      } else {
        console.error('Cart error details:', {
          variantId: variantId,
          quantity: quantity,
          error: error.message
        });
      }
      
      setTimeout(() => {
        button.classList.remove('adding');
        button.disabled = false;
        if (textSpan) textSpan.textContent = originalText;
        if (iconSpan) iconSpan.style.display = 'none';
      }, 3000);
    }
  }

  // Update cart count
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Update cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });

      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart } 
      }));
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  // Initialize on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductCards);
  } else {
    initProductCards();
  }

  // Re-initialize for dynamically loaded content
  document.addEventListener('shopify:section:load', initProductCards);
  
  // Function to initialize all product cards including in Flickity sliders
  function initAllProductCards() {
    // Initialize all visible product cards
    const allCards = document.querySelectorAll('[data-product-item]');
    allCards.forEach(card => initProductCard(card));
  }

  // Re-initialize periodically to catch Flickity sliders (runs a few times then stops)
  let initAttempts = 0;
  const maxAttempts = 5;
  const initInterval = setInterval(() => {
    initAllProductCards();
    initAttempts++;
    if (initAttempts >= maxAttempts) {
      clearInterval(initInterval);
    }
  }, 300);
  
  // For AJAX-loaded collection filters
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1 && node.matches('[data-product-item]')) {
          initProductCard(node);
        } else if (node.nodeType === 1) {
          const cards = node.querySelectorAll('[data-product-item]');
          cards.forEach(card => initProductCard(card));
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

})();

